import { ToggleButton } from '@mui/material';
import { useState } from 'react';
import { StoneXMultiselect, StoneXSelect, StoneXToggleButtonGroup } from '../../../StoneXMui';
import { StoneXRow } from '../../../StoneX';

interface MatrixChartControlsProps {
  selectedPercentiles: number[];
  onPercentilesChange: (deciles: number[]) => void;
  minPercentile: number | null;
  onMinPercentileChange: (value: number | null) => void;
  maxPercentile: number | null;
  onMaxPercentileChange: (value: number | null) => void;
}

type MatrixPreset = 'quartiles' | 'quintiles' | 'deciles' | 'custom';

const PRESET_VALUES: Record<Exclude<MatrixPreset, 'custom'>, number[]> = {
  quartiles: [0, 25, 50, 75, 100],
  quintiles: [0, 20, 40, 60, 80, 100],
  deciles: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
};

const ALL_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

function getActivePreset(selected: number[]): MatrixPreset {
  const sorted = [...selected].sort((a, b) => a - b);
  for (const [key, values] of Object.entries(PRESET_VALUES)) {
    if (sorted.length === values.length && sorted.every((v, i) => v === values[i])) {
      return key as MatrixPreset;
    }
  }
  return 'custom';
}

const MIN_DECILE_OPTIONS = [null, 0, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100];
const MAX_DECILE_OPTIONS = [null, 0, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100];

export default function MatrixChartControls({ selectedPercentiles, onPercentilesChange, minPercentile, onMinPercentileChange, maxPercentile, onMaxPercentileChange }: MatrixChartControlsProps) {
  const [mode, setMode] = useState<MatrixPreset>(() => getActivePreset(selectedPercentiles));

  function handlePresetChange(_: React.MouseEvent<HTMLElement>, value: MatrixPreset | null) {
    if (!value) { return; }
    setMode(value);
    if (value !== 'custom') {
      onPercentilesChange(PRESET_VALUES[value]);
    }
  }

  return (
    <StoneXRow style={{ alignItems: 'flex-end', gap: '16px' }}>
      <StoneXToggleButtonGroup
        value={mode}
        exclusive
        onChange={handlePresetChange}
        size="small"
        label="Decile Bands"
      >
        <ToggleButton value="quartiles">Quartiles</ToggleButton>
        <ToggleButton value="quintiles">Quintiles</ToggleButton>
        <ToggleButton value="deciles">Deciles</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </StoneXToggleButtonGroup>

      {mode === 'custom' && (
        <StoneXMultiselect
          list={ALL_OPTIONS}
          labelSelector={(x: number) => `${x}%`}
          valueSelector={(x: number) => x}
          resultSetter={onPercentilesChange}
          initial={selectedPercentiles}
          label="Custom Percentiles"
          sortComparator={(a: number, b: number) => a - b}
        />
      )}

      <StoneXSelect
        value={minPercentile}
        onChange={(value) => onMinPercentileChange(value ?? null)}
        options={MIN_DECILE_OPTIONS}
        getOptionLabel={(x) => x === null ? 'None' : `${x}%`}
        getId={(x) => x === null ? 'none' : x.toString()}
        label="Min Decile"
      />

      <StoneXSelect
        value={maxPercentile}
        onChange={(value) => onMaxPercentileChange(value ?? null)}
        options={MAX_DECILE_OPTIONS}
        getOptionLabel={(x) => x === null ? 'None' : `${x}%`}
        getId={(x) => x === null ? 'none' : x.toString()}
        label="Max Decile"
      />
    </StoneXRow>
  );
}
